import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import marissaHeadshot from '../assets/marissa-headshot.png';
import marissaProfessional from '../assets/marissa-professionnal.png';
import basicKitty from '../assets/basic-kitty.png';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeNav, setActiveNav] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(() => {
    // Check if this is the first visit to the site
    const hasVisited = sessionStorage.getItem('hasVisitedSite');
    return !hasVisited;
  });
  const [loadingProgress, setLoadingProgress] = useState(0);

  const images = [
    { src: marissaHeadshot, alt: "Marissa Creative Portrait", title: "Creative Vision" },
    { src: marissaProfessional, alt: "Marissa Professional Portrait", title: "Professional" }
  ];

  useEffect(() => {
    // Mark that the user has visited the site
    if (isLoading) {
      // Loading simulation - slower and more controlled
      const loadingInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(loadingInterval);
            setTimeout(() => {
              setIsLoading(false);
              setIsVisible(true);
              // Mark as visited after loading completes
              sessionStorage.setItem('hasVisitedSite', 'true');
            }, 800);
            return 100;
          }
          return prev + Math.random() * 8 + 2; // Slower increment between 2-10
        });
      }, 250); // Slower interval

      return () => clearInterval(loadingInterval);
    } else {
      // If not loading, show content immediately
      setIsVisible(true);
    }
  }, [isLoading]);

  useEffect(() => {
    // Image carousel interval
    const imageInterval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 4000);

    return () => clearInterval(imageInterval);
  }, [images.length]);

  const floatingElements = Array.from({ length: 12 }, (_, i) => (
    <div
      key={i}
      style={{
        position: 'absolute',
        width: Math.random() * 6 + 4 + 'px',
        height: Math.random() * 6 + 4 + 'px',
        borderRadius: '50%',
        background: `linear-gradient(45deg, 
          rgba(255, 192, 203, ${0.3 + Math.random() * 0.4}), 
          rgba(221, 160, 221, ${0.3 + Math.random() * 0.4}))`,
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
        animationDelay: Math.random() * 2 + 's',
      }}
    />
  ));

  // Loading Screen Component
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #fdf2f8 0%, #f8fafc 25%, #fce7f3 50%, #f1f5f9 75%, #fdf4ff 100%)',
        fontFamily: "'Playfair Display', 'Georgia', serif",
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Loading floating elements */}
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              borderRadius: '50%',
              background: 'rgba(219, 39, 119, 0.15)',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animation: `loading-float ${6 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: Math.random() * 3 + 's',
            }}
          />
        ))}

        {/* Loading Content */}
        <div style={{
          textAlign: 'center',
          zIndex: 10,
          maxWidth: '400px',
          padding: '2rem'
        }}>
          {/* Logo */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '2rem',
            animation: 'loading-pulse 2s ease-in-out infinite'
          }}>
            <img 
              src={basicKitty} 
              alt="Loading Logo" 
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid #db2777',
                boxShadow: '0 8px 25px rgba(219, 39, 119, 0.3)'
              }} 
            />
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '1px'
            }}>MC</div>
          </div>

          {/* Loading Text */}
          <h2 style={{
            fontSize: '1.2rem',
            fontWeight: '400',
            color: '#94a3b8',
            marginBottom: '4rem',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            opacity: 0.8
          }}>
            Loading
          </h2>

          {/* Progress Bar Container */}
          <div style={{
            width: '100%',
            maxWidth: '600px',
            height: '2px',
            background: 'rgba(148, 163, 184, 0.2)',
            borderRadius: '1px',
            overflow: 'hidden',
            marginBottom: '2rem',
            position: 'relative'
          }}>
            {/* Progress Bar */}
            <div style={{
              width: `${loadingProgress}%`,
              height: '100%',
              background: '#db2777',
              borderRadius: '1px',
              transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative'
            }} />
          </div>

          {/* Progress Percentage */}
          <div style={{
            fontSize: '0.75rem',
            color: '#cbd5e1',
            fontWeight: '400',
            letterSpacing: '1px',
            fontFamily: "'Inter', sans-serif"
          }}>
            {Math.round(loadingProgress)}%
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
          
          @keyframes loading-float {
            0%, 100% { transform: translateY(0px); opacity: 0.3; }
            50% { transform: translateY(-10px); opacity: 0.6; }
          }
          
          @keyframes loading-pulse {
            0%, 100% { transform: scale(1); opacity: 0.9; }
            50% { transform: scale(1.02); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fdf2f8 0%, #f8fafc 25%, #fce7f3 50%, #f1f5f9 75%, #fdf4ff 100%)',
      fontFamily: "'Playfair Display', 'Georgia', serif",
      color: '#2d1b3d',
      position: 'relative',
      overflow: 'hidden',
      opacity: isLoading ? 0 : 1,
      transition: 'opacity 1s ease-in-out'
    }}>
      {/* Floating decorative elements */}
      {floatingElements}
      
      {/* Enhanced Visual Effects */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '10%',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(219, 39, 119, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        animation: 'pulse 8s ease-in-out infinite',
        zIndex: 1
      }} />
      
      <div style={{
        position: 'absolute',
        top: '60%',
        right: '15%',
        width: '150px',
        height: '150px',
        background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        animation: 'pulse 6s ease-in-out infinite 2s',
        zIndex: 1
      }} />
      
      {/* Animated gradient waves */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'linear-gradient(45deg, rgba(219, 39, 119, 0.02) 0%, transparent 50%, rgba(168, 85, 247, 0.02) 100%)',
        animation: 'wave 15s ease-in-out infinite',
        zIndex: 1
      }} />
      
      {/* Sparkle effects */}
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={`sparkle-${i}`}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: '#db2777',
            borderRadius: '50%',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `sparkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: Math.random() * 2 + 's',
            zIndex: 5
          }}
        />
      ))}
      
      {/* Sophisticated Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        zIndex: 1000,
        padding: '1rem 2rem',
        borderBottom: '1px solid rgba(219, 39, 119, 0.1)',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <img 
              src={basicKitty} 
              alt="Kitty Logo" 
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #db2777',
                boxShadow: '0 2px 8px rgba(219, 39, 119, 0.2)'
              }} 
            />
            <div style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px'
            }}>MC</div>
          </div>
          <div style={{ display: 'flex', gap: '2rem' }}>
            {[
              { name: 'Home', path: '/' },
              { name: 'Gallery', path: '/gallery' },
              { name: 'Career', path: '/career' },
              { name: 'Admin', path: '/admin' }
            ].map((item, index) => (
              <Link 
                key={item.name} 
                to={item.path}
                style={{
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: activeNav === index ? '#db2777' : '#64748b',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  padding: '0.5rem 0',
                  textDecoration: 'none'
                }}
                onMouseEnter={() => setActiveNav(index)}
                onMouseLeave={() => setActiveNav(window.location.pathname === '/' ? 0 : window.location.pathname === '/career' ? 2 : 1)}
              >
                {item.name}
                {activeNav === index && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    height: '2px',
                    background: 'linear-gradient(90deg, #db2777, #ec4899)',
                    borderRadius: '1px'
                  }} />
                )}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '6rem 2rem 4rem 2rem',
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 1s ease-out',
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h1 style={{
            fontSize: 'clamp(3rem, 8vw, 5rem)',
            fontWeight: '700',
            margin: '0 0 1rem 0',
            background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
            backgroundSize: '200% 200%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-2px',
            lineHeight: '1.1',
            animation: 'gradient-shift 4s ease-in-out infinite',
            position: 'relative'
          }}>Marissa Cuevas</h1>
          <div style={{
            fontSize: '1.5rem',
            color: '#64748b',
            fontWeight: '400',
            fontStyle: 'italic',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            marginBottom: '2rem'
          }}> Single Mother & Fashionista</div>
          <div style={{
            width: '100px',
            height: '2px',
            background: 'linear-gradient(90deg, #db2777, #ec4899)',
            margin: '2rem auto',
            borderRadius: '1px'
          }} />
        </div>

        {/* Image Carousel */}
        <div style={{
          position: 'relative',
          width: '400px',
          height: '400px',
          margin: '0 auto',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
          transition: 'all 1.2s ease-out 0.3s'
        }}>
          {images.map((image, index) => (
            <div key={index} style={{
              position: 'absolute',
              inset: 0,
              opacity: currentImageIndex === index ? 1 : 0,
              transform: `scale(${currentImageIndex === index ? 1 : 0.95}) rotate(${currentImageIndex === index ? 0 : (index % 2 === 0 ? -5 : 5)}deg)`,
              transition: 'all 1s ease-in-out',
              zIndex: currentImageIndex === index ? 2 : 1
            }}>
              <img 
                src={image.src} 
                alt={image.alt} 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  border: '8px solid rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 30px 80px rgba(219, 39, 119, 0.2), 0 15px 40px rgba(0, 0, 0, 0.15)',
                  cursor: 'pointer'
                }}
                onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
              />
              <div style={{
                position: 'absolute',
                inset: '-15px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, 
                  ${index === 0 ? 'rgba(219, 39, 119, 0.1), rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1)'}`,
                zIndex: -1,
                animation: 'pulse 4s ease-in-out infinite'
              }} />
            </div>
          ))}
          
          {/* Carousel Controls */}
          <div style={{
            position: 'absolute',
            bottom: '-60px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '1rem',
            alignItems: 'center'
          }}>
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                style={{
                  width: currentImageIndex === index ? '40px' : '12px',
                  height: '12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: currentImageIndex === index 
                    ? 'linear-gradient(90deg, #db2777, #ec4899)' 
                    : 'rgba(219, 39, 119, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: currentImageIndex === index 
                    ? '0 4px 15px rgba(219, 39, 119, 0.4)' 
                    : 'none'
                }}
              />
            ))}
          </div>
          
          {/* Image Title */}
          <div style={{
            position: 'absolute',
            bottom: '-100px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            fontSize: '1.2rem',
            fontWeight: '600',
            color: '#64748b',
            fontStyle: 'italic',
            opacity: 0.8
          }}>
            {images[currentImageIndex].title}
          </div>
        </div>

      </section>

      {/* About Me Section */}
      <section style={{
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        borderRadius: '2rem',
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.08), 0 8px 25px rgba(219, 39, 119, 0.1)',
        maxWidth: '900px',
        margin: '4rem auto',
        padding: '4rem 3rem',
        textAlign: 'center',
        position: 'relative',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        zIndex: 10
      }}>
        <div style={{
          position: 'absolute',
          top: '-20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80px',
          height: '4px',
          background: 'linear-gradient(90deg, #db2777, #ec4899)',
          borderRadius: '2px'
        }} />
        
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          marginBottom: '2rem',
          background: 'linear-gradient(45deg, #db2777, #a855f7)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>About Me</h2>
        
        <p style={{
          fontSize: '1.3rem',
          color: '#475569',
          lineHeight: 1.8,
          fontWeight: '400',
          fontFamily: "'Inter', sans-serif",
          marginBottom: '2rem'
        }}>
          Hi! I'm Marissa Cuevas. I am a Chemical Engineering Major at the University of Maryland Baltimore County,
          I am into photography, fitness, and Kitty! Welcome to my portfolio!
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '3rem',
          marginTop: '3rem',
          flexWrap: 'wrap'
        }}>
          {[
            { number: '3.8', label: 'Grade Point Average' },
            { number: '4+', label: 'Projects' },
            { number: '405', label: 'Pound Bench Press' }
          ].map((stat, index) => (
            <div key={index} style={{
              textAlign: 'center',
              padding: '1.5rem',
              borderRadius: '1rem',
              background: 'linear-gradient(45deg, rgba(219, 39, 119, 0.05), rgba(168, 85, 247, 0.05))',
              border: '1px solid rgba(219, 39, 119, 0.1)'
            }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#db2777',
                marginBottom: '0.5rem'
              }}>{stat.number}</div>
              <div style={{
                fontSize: '0.9rem',
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
        marginTop: '6rem',
        padding: '4rem 2rem 2rem 2rem',
        textAlign: 'center',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, #db2777, #ec4899, transparent)'
        }} />
        
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(45deg, #db2777, #ec4899)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>Contact me</h3>
          
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
            marginBottom: '3rem',
            flexWrap: 'wrap'
          }}>
            {[
              { 
                name: 'LinkedIn', 
                url: 'https://linkedin.com', 
                icon: '💼',
                gradient: 'linear-gradient(45deg, #0077b5, #0a66c2)'
              },
              { 
                name: 'Instagram', 
                url: 'https://instagram.com', 
                icon: '📸',
                gradient: 'linear-gradient(45deg, #e4405f, #833ab4, #f77737)'
              },
              { 
                name: 'Email', 
                url: 'mailto:marissa@email.com', 
                icon: '✉️',
                gradient: 'linear-gradient(45deg, #db2777, #ec4899)'
              }
            ].map((social, index) => (
              <a
                key={social.name}
                href={social.url}
                target={social.name !== 'Email' ? '_blank' : '_self'}
                rel={social.name !== 'Email' ? 'noopener noreferrer' : undefined}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '2rem 1.5rem',
                  borderRadius: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textDecoration: 'none',
                  color: '#fff',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  minWidth: '140px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                  e.target.style.borderColor = 'rgba(219, 39, 119, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: '1rem',
                  padding: '1rem',
                  borderRadius: '50%',
                  background: social.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px'
                }}>
                  {social.icon}
                </div>
                <span style={{
                  fontWeight: '600',
                  fontSize: '1.1rem'
                }}>{social.name}</span>
              </a>
            ))}
          </div>
          
          <div style={{
            paddingTop: '2rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#9ca3af',
            fontSize: '0.9rem'
          }}>
            <p style={{ margin: 0 }}>
              © 2024 Marissa Cuevas. All rights reserved. | Crafted with 💖
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes gentle-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes wave {
          0%, 100% { transform: translateX(0) skewY(0deg); }
          25% { transform: translateX(-20px) skewY(1deg); }
          50% { transform: translateX(0) skewY(0deg); }
          75% { transform: translateX(20px) skewY(-1deg); }
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @media (max-width: 768px) {
          .carousel-container {
            width: 300px !important;
            height: 300px !important;
          }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default Home; 